import React from 'react';
import { motion, useInView } from 'framer-motion';
import { Badge } from './Badge';
import { Rough } from './Rough';
import { useTilt } from '../hooks/useTilt';
import { DUR, EASE_OUT, STAGGER, useMotionOK } from '../motion';

export type Layer = { name: string; desc: string; badge: string; own?: boolean };

function LayerBody({ layer, index }: { layer: Layer; index: number }) {
    return (
        <>
            <div>
                <span className="ds-layer__idx">{String(index + 1).padStart(2, '0')}</span>
                <div className="ds-layer__name">
                    {layer.own ? (
                        <Rough
                            type="underline"
                            color="rgba(255,255,255,0.9)"
                            strokeWidth={2.5}
                            padding={3}
                            multiline
                        >
                            {layer.name}
                        </Rough>
                    ) : (
                        layer.name
                    )}
                </div>
            </div>
            <p className="ds-layer__desc">{layer.desc}</p>
            <Badge tone={layer.own ? 'accent' : 'neutral'}>{layer.badge}</Badge>
        </>
    );
}

function OwnLayer({ layer, index, landed }: { layer: Layer; index: number; landed: boolean }) {
    const tilt = useTilt<HTMLDivElement>({ max: 3, sheen: true });
    return (
        <div
            ref={tilt.ref}
            onPointerMove={tilt.onPointerMove}
            onPointerLeave={tilt.onPointerLeave}
            className={[
                'ds-layer ds-glass ds-glass--strong ds-glass--3d ds-glass--sheen ds-glass--r-lg ds-layer--own',
                landed ? 'is-landed' : '',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            <LayerBody layer={layer} index={index} />
        </div>
    );
}

/* The stack assembles from the ground up: hardware slides in first, the A2A
   layer lands last and flashes its ring once. */
export function LayerStack({ layers }: { layers: Layer[] }) {
    const ref = React.useRef<HTMLDivElement>(null);
    const inView = useInView(ref, { once: true, margin: '-10% 0px -10% 0px' });
    const motionOK = useMotionOK();
    const [landed, setLanded] = React.useState(false);

    const lastDelay = (layers.length - 1) * STAGGER.base;

    React.useEffect(() => {
        if (!inView || !motionOK) return;
        const id = window.setTimeout(() => setLanded(true), (lastDelay + DUR.glass) * 1000);
        return () => window.clearTimeout(id);
    }, [inView, motionOK, lastDelay]);

    return (
        <div className="ds-layers" ref={ref}>
            {layers.map((layer, i) => {
                const delay = (layers.length - 1 - i) * STAGGER.base;
                const body = layer.own ? (
                    <OwnLayer layer={layer} index={i} landed={landed} />
                ) : (
                    <div className="ds-layer ds-card ds-card--r-lg">
                        <LayerBody layer={layer} index={i} />
                    </div>
                );

                if (!motionOK) return <React.Fragment key={layer.name}>{body}</React.Fragment>;

                return (
                    <motion.div
                        key={layer.name}
                        className="ds-layers__slot"
                        initial={{ opacity: 0, x: -24 }}
                        animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -24 }}
                        transition={{ duration: DUR.glass, ease: EASE_OUT, delay }}
                    >
                        {body}
                    </motion.div>
                );
            })}
        </div>
    );
}
